/**
 * NOTE: For compatibility across platforms all calls **must** return a
 * `Promise` object!
 */

import * as Platform from '../Platform';
import { PlayerAbstract } from '../PlayerAbstract';
import { BSLegacyScreenshotMixin } from './BSLegacyScreenshotMixin';
import { BSLegacyVideoAssetCacheMixin } from './BSLegacyVideoAssetCacheMixin';
import { BrightScriptMessenger } from './BrightScriptMessenger';
import { Signage } from '../../Signage/Signage';
import { Log } from '../../AuxillaryService/Log';
import { $ } from '../../../vendor/jquery';
import { moment } from '../../../vendor/moment';
import { Permission } from '../Permissions';
import { Feature, isUrlQueryPlatform, hasUrlQueryFeature } from '../PlatformCheck';
import { BrightScriptMessengerHeartbeat } from './Heartbeat';
import { escapeNamedValueKey } from '../SafeNamedValueFilename';
import { getCdnAssetInfo } from '../../ReachApiV1';

/* global BSDeviceInfo */

/**
 * @constructor
 * @augments PlayerAbstract
 * @implements HeartbeatInterface
 * @implements NamedValueCacheInterface
 * @implements PlayerControlInterface
 * @implements PlayerPackageInterface
 * @implements ScreenshotInterface
 * @implements NtpInterface
 * @implements TimeZoneInterface
 * @implements VideoOutputInterface
 * @implements VideoAssetCacheInterface
 * @implements SystemInformationInterface
 * @implements BrightSignInformationInterface
 * @implements ScreenOrientationInterface
 * @implements SignageReadyInterface
 * @mixes BSLegacyScreenshotMixin
 * @mixes BSLegacyVideoAssetCacheMixin
 * @todo merge `BrightSignSupportService` class into this class
 */
function BrightSignPlayer() {
    PlayerAbstract.call(this);

    this._messenger = new BrightScriptMessenger();
    this._bsApi = parent.ReachBS || {};
    this._heartbeat = new BrightScriptMessengerHeartbeat(this._messenger);

    // legacy screenshot retrieval (TODO: move to initialize and only apply if
    // needed)
    BSLegacyScreenshotMixin.call(this);

    // legacy video asset retrieval (TODO: move to initialize and only apply if
    // needed)
    BSLegacyVideoAssetCacheMixin.call(this);

    this.addPermission(Permission.BS_HDMI); // @todo only add for select XT models
    this.addPermission(Permission.NATIVE_HLS);
    this.addPermission(Permission.NATIVE_RTP);
    this.addPermission(Permission.RELAXED_NET_SECURITY);
}

BrightSignPlayer.prototype = Object.create(PlayerAbstract.prototype);
BrightSignPlayer.prototype.constructor = BrightSignPlayer;

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.type = Platform.BRIGHTSIGN;

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.name = 'BrightSign';

/**
 * @type {BrightScriptMessenger}
 * @protected
 * @deprecated use functions found on
 * {@link Platform.BrightSignPlayer#_bsApi} instead.
 */
BrightSignPlayer.prototype._messenger = undefined;

/**
 * The BrightScript/JavaScript communication object provided by Reach's
 * BrightSign player code.
 * @type {(ReachBS|{})}
 * @protected
 */
BrightSignPlayer.prototype._bsApi = undefined;

/**
 * Cached player version, so the BrightScript layer only needs to be asked
 * once what the version number is.
 * @type {undefined|string}
 * @protected
 */
BrightSignPlayer.prototype._playerVersion = undefined;

/**
 * @type {BrightScriptMessengerHeartbeat}
 * @protected
 */
BrightSignPlayer.prototype._heartbeat = undefined;

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.initializePlatform = async function () {
    await PlayerAbstract.prototype.initializePlatform.call(this);

    if (this._bsApi.getFeatures !== undefined) {
        /** @type {Array<string>} */
        const feats = await this._bsApi.getFeatures();
        if (feats.includes('mse-support')) {
            this.removePermission(Permission.NATIVE_HLS);
        }
    }
};

/**
 * @returns {boolean}
 */
BrightSignPlayer.isTheRunningPlatform = function () {
    const thisPlatformIsRunning = isUrlQueryPlatform('BrightSign');
    if (typeof thisPlatformIsRunning === 'boolean') {
        const thisPlatformSupportsPmc = hasUrlQueryFeature(Feature.PostMessageClient);
        return thisPlatformIsRunning && !thisPlatformSupportsPmc;
    }

    // use fallback check
    return typeof BSDeviceInfo !== 'undefined';
};

/**
 * @inheritDoc
 * @todo use native promises
 */
BrightSignPlayer.prototype.clearBrowserCacheOnReboot = function () {
    var jqDeferred = $.Deferred();

    this._messenger
        .postMessage('CLEAR_BROWSER_CACHE_ON_RESTART')
        .done(function () {
            jqDeferred.resolve();
        })
        .fail(function (messageError) {
            jqDeferred.reject(messageError);
        });

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 * @todo use native promises
 */
BrightSignPlayer.prototype.disableVideoOutput = function (disableOutput) {
    var logInfo;
    var messageCode;
    var jqDeferred = $.Deferred();

    if (disableOutput === false) {
        logInfo = 'Re-enabling Video Output';
        messageCode = 'ENABLE_VIDEO_OUTPUT';
    } else {
        disableOutput = true;
        logInfo = 'Disabling Video Output';
        messageCode = 'DISABLE_VIDEO_OUTPUT';
    }

    this._messenger
        .postMessage(messageCode)
        .done(function (messageData) {
            Log.Info(logInfo, null, __fileline);
            jqDeferred.resolve(BrightScriptMessenger.toBoolean(messageData.disable_video_output));
        })
        .fail(function (failedError) {
            Log.Error('Failed ' + logInfo, null, __fileline);
            jqDeferred.reject(failedError);
        });

    return jqDeferred.promise();
};

//START: VideoAssetCacheInterface <editor-fold desc="VideoAssetCacheInterface">
/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.cacheVideoAsset = function (assetUuid) {
    var self = this;
    return Promise.resolve()
        .then(function () {
            return self.isAtLeastVersion('2.1.0.7');
        })
        .then(function (supportsOnDemandDownloads) {
            if (!supportsOnDemandDownloads) {
                return self._legacyCacheVideoAsset(assetUuid);
            } else {
                return self._cacheStatus(assetUuid).then(function (status) {
                    var result;
                    switch (status) {
                        case 'cached':
                            result = Promise.resolve(true);
                            // already cached
                            break;
                        case 'caching':
                            result = Promise.resolve(false);
                            // nothing needs doing
                            break;
                        case 'not-cached':
                            result = Promise.resolve(false);
                            // start caching asset (no need to wait for it)
                            self._getConcreteUrl(assetUuid).then(function (concreteUrl) {
                                return self._downloadAsset(concreteUrl, assetUuid);
                            });
                            break;
                    }
                    return result;
                });
            }
        });
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getVideoPath = function (assetUuid) {
    var self = this;
    return Promise.resolve()
        .then(function () {
            return self.isAtLeastVersion('2.1.0.7');
        })
        .then(function (supportsOnDemandDownloads) {
            if (!supportsOnDemandDownloads) {
                return self._legacyGetVideoPath(assetUuid);
            } else {
                return self._cacheStatus(assetUuid).then(function (status) {
                    var result = 'file:///' + assetUuid;
                    if (status !== 'cached') {
                        result = self._getConcreteUrl(assetUuid);
                    }
                    return result;
                });
            }
        });
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.isCachedAsset = function (assetUuid) {
    var self = this;
    return Promise.resolve()
        .then(function () {
            return self.isAtLeastVersion('2.1.0.7');
        })
        .then(function (supportsOnDemandDownloads) {
            if (!supportsOnDemandDownloads) {
                return self._legacyIsCachedAsset(assetUuid);
            } else {
                return self._cacheStatus(assetUuid).then(function (status) {
                    return status === 'cached';
                });
            }
        });
};

/**
 * Get cache status for asset name
 * @private
 * @param assetUuid
 * @return {Promise.<('not-cached'|'caching'|'cached')>|Promise.<Error>}
 */
BrightSignPlayer.prototype._cacheStatus = function (assetUuid) {
    return this._queryAssets().then(function (assets) {
        var result = 'not-cached';
        if (Object.prototype.hasOwnProperty.call(assets, assetUuid)) {
            if (!assets[assetUuid]) {
                result = 'caching';
            } else {
                result = 'cached';
            }
        }
        return result;
    });
};

/**
 * UUID keys, Download status value: 0 for false, 1 for true
 * @typedef {Object.<string, boolean>} assetCacheStatuses
 */

/**
 * Get an object with keys of asset name and value of cached status either "0"
 * or "1"
 * @private
 * @return {Promise<assetCacheStatuses>|Promise<Error>}
 */
BrightSignPlayer.prototype._queryAssets = function () {
    return Promise.resolve()
        .then(
            function () {
                if (this._bsApi.queryAssets) {
                    return this._bsApi.queryAssets();
                } else {
                    return this._messenger.postMessage('QUERY_ASSETS').then(
                        function (response) {
                            // convert response to {@link assetCacheStatuses}
                            var assets = {};
                            Object.getOwnPropertyNames(response).forEach(function (prop) {
                                if (response[prop] === '0') {
                                    assets[prop] = false;
                                } else if (response[prop] === '1') {
                                    assets[prop] = true;
                                }
                            });
                            return assets;
                        }.bind(this)
                    );
                }
            }.bind(this)
        )
        .catch(logAndRethrowError('Error Querying Asset:'));
};

/**
 * Get path at end of redirection for Reach assets
 * @private
 * @param assetUuid
 * @return {Promise<string>|Promise<Error>} - URL
 */
BrightSignPlayer.prototype._getConcreteUrl = function (assetUuid) {
    return getCdnAssetInfo(assetUuid)
        .then(function (result) {
            var mp4 = result.assets.filter(function (asset) {
                return asset.type === 'video/mp4';
            })[0];
            return mp4.redirect;
        })
        .catch(logAndRethrowError('Failed getting concrete path:'));
};

/**
 * Log error with message and re-throw
 * @callback logError
 * @memberof BrightSign#
 * @param {Error} err
 * @throws {Error}
 */

/**
 * Log error with message and re-throw
 * @private
 * @param {string} message
 * @return {BrightSign#logError}
 */
function logAndRethrowError(message) {
    return function (err) {
        if (err) {
            var data = { message: err.message };
            if (err.stack) {
                data.stack = err.stack;
            }
            Log.Error(message, null, __fileline, data);
        } else {
            Log.Error(message, null, __fileline);
        }
        throw err;
    };
}

/**
 * Instruct BrightSign to download asset
 * @private
 * @param {string} url
 * @param {string} name
 * @param {number} [size]
 * @return {Promise.<{status: string, type: string}>|Promise.<Error>} - Post
 * back message
 */
BrightSignPlayer.prototype._downloadAsset = function (url, name, size) {
    var self = this;
    return Promise.resolve()
        .then(function () {
            if (size) {
                return Promise.resolve(size);
            } else {
                // Get content length of asset from head request for URL
                // (This may not be needed in later player versions)
                return $.ajax({
                    method: 'HEAD',
                    url: url,
                }).then(function (data, textStatus, jqXhr) {
                    return jqXhr.getResponseHeader('Content-Length');
                });
            }
        })
        .then(function (responseSize) {
            return self._messenger.postMessage('DOWNLOAD_ASSET', {
                link: url,
                name: name,
                size: responseSize,
            });
        })
        .catch(logAndRethrowError('Error Caching Asset:'));
};
//END: VideoAssetCacheInterface <editor-fold desc="VideoAssetCacheInterface">

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getHostPlatform = function () {
    return Promise.resolve(this.name);
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getPlayerVersion = function () {
    return Promise.resolve()
        .then(
            function () {
                if (this._playerVersion) {
                    return Promise.resolve(this._playerVersion);
                } else if (this._bsApi.getPlayerVersion) {
                    return this._bsApi.getPlayerVersion();
                } else {
                    return this._messenger
                        .postMessage('GET_PLAYER_VERSION')
                        .then(function (messageData) {
                            return messageData.version;
                        });
                }
            }.bind(this)
        )
        .then(
            function (version) {
                if (!this._playerVersion) {
                    this._playerVersion = version;
                }
                return version;
            }.bind(this)
        );
};

/**
 * returns a device-specific hardware ID based on BrightSign serial number.
 *
 * @return {Promise<string>}
 */
BrightSignPlayer.prototype._generateAndGetHardwareId = function () {
    return Promise.resolve()
        .then(Signage.SystemInformation)
        .then(function (systemData) {
            return 'BrightSign://' + systemData.brightsign.deviceUniqueId;
        });
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getScreenshot = function () {
    return Promise.resolve()
        .then(
            function () {
                return this.isAtLeastVersion('2.1.2.0');
            }.bind(this)
        )
        .then(
            function (supportsScreenshotRev2) {
                if (this._bsApi.getScreenshot) {
                    return this._bsApi.getScreenshot();
                } else if (supportsScreenshotRev2) {
                    return this._messenger.postMessage('GET_SCREENSHOT_REV2');
                } else {
                    return this._getLegacyScreenshot();
                }
            }.bind(this)
        )
        .then(
            function (messageData) {
                return {
                    base64Image: messageData.data,
                    mimeType: messageData.mime_type,
                };
            }.bind(this)
        );
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.rebootSystem = function () {
    return new Promise(
        function () {
            // this promise will never resolve (this is on purpose)
            var attemptReboot = async function () {
                try {
                    await this.setNamedValue(
                        'last_restart',
                        String(moment().format('YYYY-MM-DD HH:mm:ss'))
                    );
                } catch (err) {
                    Log.Warning(
                        'Failed to set the `last_restart` NamedValueCache variable.',
                        null,
                        __fileline,
                        err
                    );
                }

                // still make sure to reboot even if `last_restart` does not get set
                if (this._bsApi.rebootSystem) {
                    this._bsApi.rebootSystem();
                } else {
                    this._messenger.postMessage('REBOOT_SYSTEM');
                }

                Log.Notice('If reboot fails, another attempt will be tried in 1 second.');
                // NOTE: this is a kludge to fix a problem where some messages are
                // missed by BrightScript
                //
                // 2021-02-12 JJJ: This may no longer be necessary as the messaging
                // system between BrightScript and JavaScript is now more robust.
                setTimeout(attemptReboot, 1000);
            }.bind(this);

            attemptReboot();
        }.bind(this)
    );
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.startSendingHeartbeat = function () {
    this._heartbeat.start();
};

//START: NamedValueCache <editor-fold desc="NamedValueCache">
/**
 * @inheritDoc
 * @todo use native promises
 */
BrightSignPlayer.prototype.deleteNamedValue = function (keyName) {
    var jqDeferred = $.Deferred();
    var params = { key: escapeNamedValueKey(keyName) };

    this._messenger
        .postMessage('DELETE_NAMED_VALUE', params)
        .done(function () {
            jqDeferred.resolve();
        })
        .fail(function (messageError) {
            jqDeferred.reject(messageError);
        });

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 * @todo use native promises
 */
BrightSignPlayer.prototype.getNamedValue = function (keyName, defaultValue) {
    var jqDeferred = $.Deferred();
    var invalid = '__INVALID__';
    var params = {
        key: escapeNamedValueKey(keyName),
        defaultValue: invalid,
    };

    this._messenger
        .postMessage('GET_NAMED_VALUE', params)
        .done(function (messageData) {
            var keyValue = messageData.value;

            if (keyValue === invalid) {
                keyValue = defaultValue;
            }

            jqDeferred.resolve(keyValue);
        })
        .fail(function (messageError) {
            jqDeferred.reject(messageError);
        });

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 * @todo use native promises
 */
BrightSignPlayer.prototype.setNamedValue = function (keyName, keyValue) {
    var jqDeferred = $.Deferred();
    var params = {
        key: escapeNamedValueKey(keyName),
        value: keyValue,
    };

    this._messenger
        .postMessage('SET_NAMED_VALUE', params)
        .done(function () {
            jqDeferred.resolve();
        })
        .fail(function (messageError) {
            jqDeferred.reject(messageError);
        });

    return jqDeferred.promise();
};
//END: NamedValueCache </editor-fold desc="NamedValueCache">

//START: Package Deployment <editor-fold desc="Package Deployment">
/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getInstalledPackages = function () {
    return Promise.resolve()
        .then(
            function () {
                return this._messenger.postMessage('GET_INSTALLED_PACKAGES');
            }.bind(this)
        )
        .then(
            function (messageData) {
                var detail = messageData.json_encoded_detail
                    ? JSON.parse(messageData.json_encoded_detail)
                    : messageData.detail;
                return detail.packages.map(function (pkg) {
                    return {
                        Guid: pkg.guid,
                        Name: pkg.name,
                        VersionAsString: pkg.version,
                        Description: pkg.description,
                    };
                });
            }.bind(this)
        );
};

/**
 * @inheritDoc
 * @todo use native promises
 */
BrightSignPlayer.prototype.deployPackage = function (
    notificationChannel,
    playerUuid,
    guid,
    version,
    purgeDownloadedPackage
) {
    var jqDeferred = $.Deferred();
    var params = {
        notificationChannel: notificationChannel,
        playerUuid: playerUuid,
        guid: guid,
        version: version,
        purgeDownloadedPackage: purgeDownloadedPackage,
    };

    this._messenger
        .postMessage('DEPLOY_PACKAGE', params)
        .done(function () {
            jqDeferred.resolve();
        })
        .fail(function (messageError) {
            jqDeferred.reject(messageError);
        });

    return jqDeferred.promise();
    /*
    return Promise.resolve()
        .then(function() {
            return this._messenger.postMessage("DEPLOY_PACKAGE", params);
        }.bind(this))
        .then(function() {
            return Promise.resolve();
        });
    */
};
//END: Package Deployment </editor-fold desc="Package Deployment">

//START: TimeZoneInterface </editor-fold desc="TimeZoneInterface">
/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.setTimeZone = function (timeZone) {
    return Promise.resolve()
        .then(
            function () {
                // @todo check time zones against available brightsign zones
                return this._messenger.postMessage('SET_SYSTEM_TIME_ZONE', { timeZone: timeZone });
            }.bind(this)
        )
        .then(
            function (response) {
                return {
                    rebootRequired: response.restart_required === '1' ? true : false,
                };
            }.bind(this)
        );
};
//END: TimeZoneInterface </editor-fold desc="TimeZoneInterface">

//START: NtpInterface </editor-fold desc="NtpInterface">
/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.enableNtp = async function (server, interval) {
    const params = { server, interval };
    await this._messenger.postMessage('SET_TIME_SERVER', params);
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.disableNtp = function () {
    // Do nothing. Not sending `SET_TIME_SERVER` already allows external control of NTP.
    return Promise.resolve();
};

//END: NtpInterface </editor-fold desc="NtpInterface">

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.sendSignageReadySignal = async function () {
    await this._messenger.postMessage('AUX_SVC_INIT');
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.setOrientation = async function (orientation) {
    await this._messenger.postMessage('SET_ORIENTATION', { orientation });
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getBsDeviceInfo = function () {
    return Promise.resolve().then(() => {
        const brightsignInfo = new BSDeviceInfo();
        return {
            family: brightsignInfo.family,
            deviceUniqueId: brightsignInfo.deviceUniqueId,
            bootVersion: brightsignInfo.bootVersion,
            deviceBootCount: brightsignInfo.deviceBootCount,
            deviceLifetime: brightsignInfo.deviceLifetime,
            deviceUptime: brightsignInfo.deviceUptime,
            version: brightsignInfo.version,
            model: brightsignInfo.model,
        };
    });
};

/**
 * @inheritDoc
 */
BrightSignPlayer.prototype.getSystemInformation = async function () {
    const hostPlatform = await this.getHostPlatform();
    const brightsignInfo = await this.getBsDeviceInfo();

    return {
        HardwareInfo: {
            Manufacturer: hostPlatform,
            Model: brightsignInfo.model,
        },
        OperatingSystemInfo: {
            Name: hostPlatform,
            Manufacturer: hostPlatform,
            Version: brightsignInfo.version,
        },
    };
};

export { BrightSignPlayer };
